import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Autocomplete,
  Tooltip,
  InputAdornment,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  InfoOutlined as InfoIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Settings as SettingsIcon,
  Tune as TuneIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import onboardingConfigApi from '../../services/onboardingConfigApi';
import { currentUserCode } from '../../services/ccOnboardingApi';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch (e) {
    return String(dateStr);
  }
};

const C = {
  primary: '#1C6CE5',
  primaryHover: '#1557B0',
  primaryLight: '#E1F4FF',
  primaryBorder: '#93C5FD',
  textDark: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#64748B',
  bgLight: '#F8FAFC',
  bgCard: '#FFFFFF',
  border: '#94A3B8',
  borderLight: '#CBD5E1',
  inputBorder: '#94A3B8',
  inputBorderHover: '#475569',
  success: '#10B981',
  successBg: '#ECFDF5',
  successText: '#065F46',
  danger: '#EF4444',
  dangerBg: '#FEF2F2',
  dangerText: '#991B1B',
  badgeBg: '#F1F5F9',
};

const DEFAULT_MASTER_SCREENS = [
  { screen: 1, screenName: 'Order Dashboard' },
  { screen: 2, screenName: 'Order' },
  { screen: 3, screenName: 'Mobilab Onboarding' },
  { screen: 4, screenName: 'Lab Support' },
  { screen: 5, screenName: 'Lab Onboarding' },
  { screen: 6, screenName: 'CC Onboarding' },
  { screen: 7, screenName: 'Business Approval' },
  { screen: 8, screenName: 'Finance Approval' },
  { screen: 9, screenName: 'Configuration' },
  { screen: 10, screenName: 'Corporate' },
];

const DEFAULT_ROLE_OPTIONS = [
  { value: 'SUBMITTER', label: 'Submitter (Creator)' },
  { value: 'BUSINESS_APPROVER', label: 'Business Approver' },
  { value: 'FINANCE', label: 'Finance Approver' },
];

const getInitials = (name) => {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const getAvatarColor = (name) => {
  const colors = [
    { bg: '#EDE9FE', color: '#6D28D9' },
    { bg: '#E0F2FE', color: '#0369A1' },
    { bg: '#FEF3C7', color: '#B45309' },
    { bg: '#DCFCE7', color: '#15803D' },
    { bg: '#FCE7F3', color: '#BE185D' },
    { bg: '#FEE2E2', color: '#B91C1C' },
    { bg: '#CCFBF1', color: '#0F766E' },
  ];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export function findMatchingState(val, states = []) {
  if (!val) return null;
  const sStr = String(val).trim();
  if (!sStr) return null;

  return (
    states.find((s) => {
      if (!s) return false;
      if (typeof s === 'string') {
        return s.toLowerCase() === sStr.toLowerCase() || (sStr.length <= 3 && sStr.toUpperCase() === s.toUpperCase());
      }
      const sCode = String(s.stateCode || s.state_code || s.code || s.value || '').toUpperCase();
      const sName = String(s.stateName || s.state_name || s.name || s.label || '').toLowerCase();
      const searchUpper = sStr.toUpperCase();
      const searchLower = sStr.toLowerCase();

      return (
        sCode === searchUpper ||
        sName === searchLower ||
        (sCode === 'OD' && (searchUpper === 'OR' || searchLower === 'odisha')) ||
        (sCode === 'OR' && (searchUpper === 'OD' || searchLower === 'odisha')) ||
        (sName === 'odisha' && (searchUpper === 'OD' || searchUpper === 'OR')) ||
        (searchLower === 'odisha' && (sCode === 'OD' || sCode === 'OR'))
      );
    }) || null
  );
}

export default function Configuration() {
  const [activeTab, setActiveTab] = useState(0);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [stateList, setStateList] = useState([]);
  const [loadingStates, setLoadingStates] = useState(true);

  const [userConfigData, setUserConfigData] = useState(null);
  const [loadingUserConfig, setLoadingUserConfig] = useState(false);
  const [userConfigurations, setUserConfigurations] = useState([]);
  const [editingConfigId, setEditingConfigId] = useState(null);
  const editingConfigIdRef = useRef(null);
  const setEditingConfig = (id) => {
    editingConfigIdRef.current = id;
    setEditingConfigId(id);
  };
  const [showAddConfigForm, setShowAddConfigForm] = useState(false);
  const [viewMode, setViewMode] = useState('selected'); // 'selected' | 'all'
  const [allUserConfigurations, setAllUserConfigurations] = useState([]);
  const [loadingAllConfigs, setLoadingAllConfigs] = useState(false);
  const [allConfigTab, setAllConfigTab] = useState('CC');
  const [allConfigPagination, setAllConfigPagination] = useState({
    page: 0,
    size: 50,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false,
  });

  const [workflowForm, setWorkflowForm] = useState({
    id: null,
    onboardingType: 'LAB',
    roleCode: 'SUBMITTER',
    stateCode: '',
    stateName: 'All States (Default)',
    autoApprove: 0,
    status: 1,
  });
  const [submittingWorkflow, setSubmittingWorkflow] = useState(false);

  const [masterScreens, setMasterScreens] = useState(DEFAULT_MASTER_SCREENS);
  const [loadingMasterScreens, setLoadingMasterScreens] = useState(false);
  const [loadingUserScreens, setLoadingUserScreens] = useState(false);

  const [assignedScreens, setAssignedScreens] = useState([]);
  const [initialAssignedScreens, setInitialAssignedScreens] = useState([]);
  const [screenSearchFilter, setScreenSearchFilter] = useState('');
  const [submittingScreens, setSubmittingScreens] = useState(false);

  const [rightPanelMode, setRightPanelMode] = useState('config'); // 'config' | 'log'
  const [configLogs, setConfigLogs] = useState([]);
  const [loadingConfigLogs, setLoadingConfigLogs] = useState(false);
  const [configLogsPagination, setConfigLogsPagination] = useState({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 1,
  });

  const fetchUsers = useCallback(async (search = '') => {
    setLoadingUsers(true);
    try {
      const userList = await onboardingConfigApi.getUsers({ search, roleCode: 'DIAGGROUPADMIN' });
      const diagGroupAdminOnly = (userList || []).filter((u) => {
        const r = u.userRoleCode || u.roleCode || u.role;
        return !r || r.toUpperCase() === 'DIAGGROUPADMIN';
      });
      setUsers(diagGroupAdminOnly);
      if (diagGroupAdminOnly.length > 0) {
        setSelectedUser((prev) => {
          if (!prev) return diagGroupAdminOnly[0];
          const found = diagGroupAdminOnly.find(
            (u) => (u.userCode || u.code || u.id) === (prev.userCode || prev.code || prev.id)
          );
          return found || diagGroupAdminOnly[0];
        });
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Failed to load users list');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchStates = useCallback(async () => {
    setLoadingStates(true);
    try {
      const states = await onboardingConfigApi.getStateList();
      setStateList(states);
    } catch (err) {
      console.error('Error loading states:', err);
    } finally {
      setLoadingStates(false);
    }
  }, []);

  const fetchMasterScreens = useCallback(async () => {
    setLoadingMasterScreens(true);
    try {
      const list = await onboardingConfigApi.getAllScreens();
      if (Array.isArray(list) && list.length > 0) {
        setMasterScreens(list);
      }
    } catch (err) {
      console.error('Error loading master screens:', err);
    } finally {
      setLoadingMasterScreens(false);
    }
  }, []);

  const loadUserScreens = useCallback(async (uId) => {
    if (!uId) {
      setAssignedScreens([]);
      setInitialAssignedScreens([]);
      return;
    }
    setLoadingUserScreens(true);
    try {
      const configured = await onboardingConfigApi.getUserScreens(uId);
      const activeScreens = (Array.isArray(configured) ? configured : [])
        .filter((item) => item.status === undefined || Number(item.status) === 1)
        .map((item) => ({
          screen: Number(item.screen),
          screenName: item.screenName || `Screen ${item.screen}`,
        }));
      setAssignedScreens(activeScreens);
      setInitialAssignedScreens(activeScreens);
    } catch (err) {
      console.error('Error loading user screens:', err);
    } finally {
      setLoadingUserScreens(false);
    }
  }, []);

  const loadUserConfigLogs = useCallback(async (uId, page = 0, size = 10) => {
    if (!uId) {
      setConfigLogs([]);
      setConfigLogsPagination({ page: 0, size: 10, totalElements: 0, totalPages: 0 });
      return;
    }
    setLoadingConfigLogs(true);
    try {
      const res = await onboardingConfigApi.getUserConfigLogs({ userId: uId, page, size });
      if (res && Array.isArray(res.content)) {
        setConfigLogs(res.content);
        if (res.page) {
          setConfigLogsPagination({
            page: res.page.number ?? page,
            size: res.page.size ?? size,
            totalElements: res.page.totalElements ?? res.content.length,
            totalPages: res.page.totalPages ?? 1,
          });
        }
      }
    } catch (err) {
      console.error('Error loading user config logs:', err);
    } finally {
      setLoadingConfigLogs(false);
    }
  }, []);

  const handleLogPageChange = (event, newPage) => {
    const uCode = selectedUser?.userCode || selectedUser?.userId || selectedUser?.code || selectedUser?.id;
    loadUserConfigLogs(uCode, newPage, configLogsPagination.size);
  };

  const handleLogRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    const uCode = selectedUser?.userCode || selectedUser?.userId || selectedUser?.code || selectedUser?.id;
    loadUserConfigLogs(uCode, 0, newSize);
  };

  useEffect(() => {
    fetchUsers(searchQuery);
    fetchStates();
    fetchMasterScreens();
  }, [fetchUsers, fetchStates, fetchMasterScreens]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    fetchUsers(searchQuery.trim());
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    fetchUsers('');
  };

  const loadUserConfig = useCallback(async (uCode) => {
    if (!uCode) return;
    setLoadingUserConfig(true);
    try {
      const res = await onboardingConfigApi.getUserWorkflowConfig(uCode);
      setUserConfigData(res);

      let configs = [];
      if (Array.isArray(res?.configurations)) {
        configs = res.configurations;
      } else if (Array.isArray(res?.content)) {
        configs = res.content;
      } else if (Array.isArray(res?.data?.configurations)) {
        configs = res.data.configurations;
      } else if (Array.isArray(res?.data)) {
        configs = res.data;
      } else if (Array.isArray(res?.responseData?.configurations)) {
        configs = res.responseData.configurations;
      } else if (Array.isArray(res)) {
        configs = res;
      } else if (res?.workflowConfig) {
        configs = [res.workflowConfig];
      } else if (res?.data?.workflowConfig) {
        configs = [res.data.workflowConfig];
      } else if (res?.responseData?.workflowConfig) {
        configs = [res.responseData.workflowConfig];
      } else if (res?.onboardingType || res?.roleCode || res?.stateCode || res?.state) {
        configs = [res];
      } else if (res?.data?.onboardingType || res?.data?.roleCode || res?.data?.stateCode || res?.data?.state) {
        configs = [res.data];
      } else if (res?.responseData?.onboardingType || res?.responseData?.roleCode || res?.responseData?.stateCode || res?.responseData?.state) {
        configs = [res.responseData];
      }

      setUserConfigurations(configs);
      if (!editingConfigIdRef.current) {
        setEditingConfig(null);
        setShowAddConfigForm(false);

        const config0 = configs.length > 0 ? configs[0] : null;

        if (config0) {
          let oType = String(config0.onboardingType || 'LAB').toUpperCase();
          if (oType.includes('COLLECTION') || oType === 'CC') {
            oType = 'CC';
          } else {
            oType = 'LAB';
          }

          let rCode = config0.roleCode || config0.role || 'SUBMITTER';
          if (rCode === 'CC_ONBOARD' || rCode === 'LAB_ONBOARD' || rCode === 'ONBOARD') {
            rCode = 'SUBMITTER';
          } else if (rCode === 'BUSINESS') {
            rCode = 'BUSINESS_APPROVER';
          }

          const rawStateVal = config0.stateCode || config0.state_code || config0.state || config0.stateName || config0.state_name || '';
          const rawStateName = config0.stateName || config0.state_name || (config0.state && String(config0.state).length > 3 ? config0.state : '') || '';
          const rawStateCode = config0.stateCode || config0.state_code || (config0.state && String(config0.state).length <= 3 ? String(config0.state).toUpperCase() : '') || '';

          const matched = findMatchingState(rawStateVal || rawStateName || rawStateCode, stateList);
          const resolvedStateCode = matched
            ? (matched.stateCode || matched.state_code || matched.code || rawStateCode || '')
            : (rawStateCode || (rawStateVal && rawStateVal.length <= 3 ? rawStateVal.toUpperCase() : ''));
          const resolvedStateName = matched
            ? (matched.stateName || matched.state_name || matched.name || matched.label || rawStateName || resolvedStateCode)
            : (rawStateName || resolvedStateCode || 'All States (Default)');

          setWorkflowForm({
            id: config0.id || null,
            onboardingType: oType,
            roleCode: rCode,
            stateCode: resolvedStateCode,
            stateName: resolvedStateCode ? resolvedStateName : 'All States (Default)',
            autoApprove: config0.autoApprove !== undefined ? Number(config0.autoApprove) : 0,
            status: config0.status !== undefined ? Number(config0.status) : 1,
          });
        } else {
          setWorkflowForm({
            id: null,
            onboardingType: 'LAB',
            roleCode: 'SUBMITTER',
            stateCode: '',
            stateName: 'All States (Default)',
            autoApprove: 0,
            status: 1,
          });
        }
      }
    } catch (err) {
      console.error('Error loading user config:', err);
    } finally {
      setLoadingUserConfig(false);
    }
  }, [stateList]);

  useEffect(() => {
    if (!stateList.length || !userConfigData || editingConfigIdRef.current) return;

    let configs = [];
    if (Array.isArray(userConfigData?.configurations)) {
      configs = userConfigData.configurations;
    } else if (Array.isArray(userConfigData?.content)) {
      configs = userConfigData.content;
    } else if (Array.isArray(userConfigData?.data?.configurations)) {
      configs = userConfigData.data.configurations;
    } else if (Array.isArray(userConfigData?.data)) {
      configs = userConfigData.data;
    } else if (Array.isArray(userConfigData?.responseData?.configurations)) {
      configs = userConfigData.responseData.configurations;
    } else if (Array.isArray(userConfigData)) {
      configs = userConfigData;
    } else if (userConfigData?.workflowConfig) {
      configs = [userConfigData.workflowConfig];
    } else if (userConfigData?.data?.workflowConfig) {
      configs = [userConfigData.data.workflowConfig];
    } else if (userConfigData?.responseData?.workflowConfig) {
      configs = [userConfigData.responseData.workflowConfig];
    } else if (userConfigData?.onboardingType || userConfigData?.roleCode || userConfigData?.stateCode || userConfigData?.state) {
      configs = [userConfigData];
    } else if (userConfigData?.data?.onboardingType || userConfigData?.data?.roleCode || userConfigData?.data?.stateCode || userConfigData?.data?.state) {
      configs = [userConfigData.data];
    } else if (userConfigData?.responseData?.onboardingType || userConfigData?.responseData?.roleCode || userConfigData?.responseData?.stateCode || userConfigData?.responseData?.state) {
      configs = [userConfigData.responseData];
    }

    const config0 = configs.length > 0 ? configs[0] : null;
    if (config0) {
      const rawStateVal = config0.stateCode || config0.state_code || config0.state || config0.stateName || config0.state_name || '';
      if (rawStateVal) {
        const matched = findMatchingState(rawStateVal, stateList);
        if (matched) {
          const sCode = matched.stateCode || matched.state_code || matched.code || '';
          const sName = matched.stateName || matched.state_name || matched.name || matched.label || sCode;
          setWorkflowForm((prev) => ({
            ...prev,
            stateCode: sCode || prev.stateCode,
            stateName: sName || prev.stateName,
          }));
        }
      }
    }
  }, [stateList, userConfigData]);

  useEffect(() => {
    if (!selectedUser) return;
    const uCode = selectedUser.userCode || selectedUser.userId || selectedUser.code || selectedUser.id;
    loadUserConfig(uCode);
    loadUserScreens(uCode);
    if (activeTab === 0) {
      loadUserConfigLogs(uCode, 0, 10);
    }
  }, [selectedUser, activeTab, loadUserConfig, loadUserScreens, loadUserConfigLogs]);

  const parseRow = useCallback(
    (item, parentUser = null, idx = 0) => {
      if (!item) return null;
      const uCode =
        item.userCode ||
        item.userId ||
        item.code ||
        parentUser?.userCode ||
        parentUser?.userId ||
        parentUser?.code ||
        '';
      const matchedUser =
        (users || []).find(
          (u) =>
            String(u.userCode || u.userId || u.code || u.id || '').toUpperCase() ===
            String(uCode).toUpperCase()
        ) || parentUser;

      const uName =
        item.userName ||
        item.fullName ||
        matchedUser?.userName ||
        matchedUser?.fullName ||
        `${matchedUser?.firstName || ''} ${matchedUser?.lastName || ''}`.trim() ||
        uCode ||
        'Unknown User';

      let oType = String(item.onboardingType || item.onboarding_type || item.onboardingConfiguration || '').toUpperCase();
      if (oType.includes('COLLECTION') || oType === 'CC' || oType.includes('CC_')) {
        oType = 'CC';
      } else if (oType.includes('LAB') || oType === 'LAB') {
        oType = 'LAB';
      } else if (item.screenName) {
        oType = item.screenName;
      } else {
        oType = 'LAB';
      }

      let rCode = item.roleCode || item.role || item.roleName || 'SUBMITTER';
      if (rCode === 'CC_ONBOARD' || rCode === 'LAB_ONBOARD' || rCode === 'ONBOARD') {
        rCode = 'SUBMITTER';
      } else if (rCode === 'BUSINESS') {
        rCode = 'BUSINESS_APPROVER';
      }

      const rawStateVal = item.stateCode || item.state_code || item.state || item.stateName || item.state_name || '';
      const rawStateName = item.stateName || item.state_name || (item.state && String(item.state).length > 3 ? item.state : '') || '';
      const rawStateCode = item.stateCode || item.state_code || (item.state && String(item.state).length <= 3 ? String(item.state).toUpperCase() : '') || '';

      const matched = findMatchingState(rawStateVal || rawStateName || rawStateCode, stateList);
      const resolvedStateCode = matched
        ? (matched.stateCode || matched.state_code || matched.code || rawStateCode || '')
        : (rawStateCode || (rawStateVal && rawStateVal.length <= 3 ? rawStateVal.toUpperCase() : ''));
      const resolvedStateName = matched
        ? (matched.stateName || matched.state_name || matched.name || matched.label || rawStateName || resolvedStateCode)
        : (rawStateName || resolvedStateCode || 'All States (Default)');

      return {
        id: item.id || `${uCode}-${idx}-${Math.random().toString(36).substr(2, 6)}`,
        userCode: uCode,
        userId: uCode,
        userName: uName,
        userEmail: matchedUser?.email || item.email || (uCode ? `${uCode}@curebay.com` : ''),
        onboardingType: oType,
        onboardingTypeLabel: oType === 'CC' ? 'Collection Center' : oType === 'LAB' ? 'Lab' : oType,
        roleCode: rCode,
        roleName:
          rCode === 'SUBMITTER'
            ? 'Submitter'
            : rCode === 'BUSINESS_APPROVER'
            ? 'Business Approver'
            : rCode === 'FINANCE'
            ? 'Finance Approver'
            : item.actionDescription || rCode,
        actionDescription: item.actionDescription || '',
        stateCode: resolvedStateCode,
        stateName: resolvedStateCode ? resolvedStateName : 'All States (Default)',
        allStates: !resolvedStateCode,
        autoApprove: item.autoApprove !== undefined ? Number(item.autoApprove) : 0,
        status: item.status !== undefined && item.status !== null ? Number(item.status) : 1,
        raw: item,
      };
    },
    [users, stateList]
  );

  const loadAllConfigurations = useCallback(
    async (page = 0, size = 50, targetTab = allConfigTab) => {
      setLoadingAllConfigs(true);
      try {
        const res = await onboardingConfigApi.searchWorkflowConfigs({
          page,
          size,
          status: 1,
          onboardingType: targetTab,
        });

        let rawContent = [];
        let totalElements = 0;
        let totalPages = 0;
        let isFirst = page === 0;
        let isLast = false;
        let pageNum = page;
        let pageSize = size;

        if (res) {
          if (Array.isArray(res.content)) {
            rawContent = res.content;
          } else if (Array.isArray(res)) {
            rawContent = res;
          } else if (Array.isArray(res.data?.content)) {
            rawContent = res.data.content;
          } else if (Array.isArray(res.data)) {
            rawContent = res.data;
          }

          pageNum = typeof res.number === 'number' ? res.number : (res.pageable?.pageNumber ?? page);
          pageSize = typeof res.size === 'number' ? res.size : (res.pageable?.pageSize ?? size);
          isFirst = res.first ?? (pageNum === 0);
          isLast = res.last ?? false;

          if (typeof res.totalElements === 'number') {
            totalElements = res.totalElements;
          } else if (typeof res.data?.totalElements === 'number') {
            totalElements = res.data.totalElements;
          } else if (isLast) {
            totalElements = pageNum * pageSize + (res.numberOfElements ?? rawContent.length);
          } else {
            totalElements = (pageNum + 2) * pageSize;
          }

          if (typeof res.totalPages === 'number') {
            totalPages = res.totalPages;
          } else if (typeof res.data?.totalPages === 'number') {
            totalPages = res.data.totalPages;
          } else {
            totalPages = Math.ceil(totalElements / (pageSize || 1)) || 1;
          }
        }

        const parsedConfigs = rawContent
          .map((item, idx) => parseRow(item, null, idx))
          .filter(Boolean);

        setAllUserConfigurations(parsedConfigs);
        setAllConfigPagination({
          page: pageNum,
          size: pageSize,
          totalElements,
          totalPages,
          first: isFirst,
          last: isLast,
        });
      } catch (err) {
        console.error('Error in loadAllConfigurations:', err);
      } finally {
        setLoadingAllConfigs(false);
      }
    },
    [allConfigTab, parseRow]
  );

  const handlePageChange = (newPage) => {
    setAllConfigPagination((prev) => ({ ...prev, page: newPage }));
    loadAllConfigurations(newPage, allConfigPagination.size, allConfigTab);
  };

  const handleRowsPerPageChange = (newSize) => {
    setAllConfigPagination((prev) => ({ ...prev, size: newSize, page: 0 }));
    loadAllConfigurations(0, newSize, allConfigTab);
  };

  const handleAllConfigTabChange = (event, val) => {
    setAllConfigTab(val);
    setAllConfigPagination((prev) => ({ ...prev, page: 0 }));
    loadAllConfigurations(0, allConfigPagination.size, val);
  };

  useEffect(() => {
    if (activeTab === 1 && viewMode === 'all') {
      loadAllConfigurations(allConfigPagination.page, allConfigPagination.size, allConfigTab);
    }
  }, [activeTab, viewMode, allConfigTab, loadAllConfigurations]);

  const filteredAllConfigs = useMemo(() => {
    return allUserConfigurations.filter((cfg) => {
      const oType = String(cfg.onboardingType || '').toUpperCase();
      if (allConfigTab === 'CC') {
        return oType === 'CC' || oType.includes('COLLECTION');
      } else {
        return oType === 'LAB' || (!oType.includes('COLLECTION') && oType !== 'CC');
      }
    });
  }, [allUserConfigurations, allConfigTab]);

  const paginationCount = useMemo(() => {
    if (typeof allConfigPagination.totalElements === 'number' && allConfigPagination.totalElements > 0) {
      if (filteredAllConfigs.length === allUserConfigurations.length) {
        return allConfigPagination.totalElements;
      }
      return filteredAllConfigs.length;
    }
    if (allConfigPagination.last) {
      return allConfigPagination.page * allConfigPagination.size + filteredAllConfigs.length;
    }
    return -1;
  }, [allConfigPagination, filteredAllConfigs, allUserConfigurations]);

  const handleWorkflowChange = (field, value) => {
    setWorkflowForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStateSelect = (event, selectedOption) => {
    if (!selectedOption || selectedOption.stateCode === '' || selectedOption.stateCode === null) {
      setWorkflowForm((prev) => ({
        ...prev,
        stateCode: '',
        stateName: 'All States (Default)',
      }));
      return;
    }
    const sCode = selectedOption.stateCode || selectedOption.code || '';
    const sName = selectedOption.stateName || selectedOption.name || sCode;
    setWorkflowForm((prev) => ({
      ...prev,
      stateCode: sCode,
      stateName: sName,
    }));
  };

  const stateOptionsWithAll = useMemo(() => {
    const allOption = { id: 0, stateName: 'All States (Default)', stateCode: '' };
    const normalized = (stateList || []).map((s, idx) => {
      if (typeof s === 'string') {
        return { id: idx + 1, stateName: s, stateCode: s.length <= 3 ? s.toUpperCase() : '' };
      }
      return {
        id: s.id ?? idx + 1,
        stateName: s.stateName ?? s.state_name ?? s.name ?? s.label ?? s.stateCode ?? s.state_code ?? '',
        stateCode: s.stateCode ?? s.state_code ?? s.code ?? s.value ?? '',
      };
    });
    return [allOption, ...normalized];
  }, [stateList]);

  const handleWorkflowSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!selectedUser) {
      toast.warn('Please select a user first');
      return;
    }

    setSubmittingWorkflow(true);
    const uCode = selectedUser.userCode || selectedUser.code || selectedUser.userId || selectedUser.id || '';
    try {
      const payload = {
        userCode: uCode,
        onboardingType: workflowForm.onboardingType,
        roleCode: workflowForm.roleCode,
        stateCode: workflowForm.stateCode || '',
        autoApprove: Number(workflowForm.autoApprove),
        status: Number(workflowForm.status),
        updatedBy: currentUserCode() || 'ADMIN',
      };

      if (workflowForm.id) {
        payload.id = workflowForm.id;
      }

      const res = await onboardingConfigApi.saveWorkflowConfig(payload);
      if (res) {
        toast.success(res.responseMessage || `Configuration saved successfully!`);
        await loadUserConfig(uCode);
        if (viewMode === 'all') await loadAllConfigurations(allConfigPagination.page, allConfigPagination.size, allConfigTab);
        fetchUsers(searchQuery);
        setShowAddConfigForm(false);
        setEditingConfig(null);
      }
    } catch (err) {
      console.error('Error saving configuration:', err);
      toast.error(err.message || 'Failed to save configuration');
    } finally {
      setSubmittingWorkflow(false);
    }
  };

  const handleEditConfigRow = (config) => {
    const cfgUserCode = config.userCode || config.userId;
    if (cfgUserCode && (selectedUser?.userCode !== cfgUserCode && selectedUser?.code !== cfgUserCode && selectedUser?.id !== cfgUserCode)) {
      const matched = users.find((u) => (u.userCode || u.code || u.userId || u.id) === cfgUserCode);
      if (matched) {
        setSelectedUser(matched);
      } else {
        setSelectedUser({
          userCode: cfgUserCode,
          userName: config.userName || cfgUserCode,
          email: config.email,
        });
      }
    }

    setViewMode('selected');
    setEditingConfig(config.id);
    setShowAddConfigForm(true);

    let oType = String(config.onboardingType || 'LAB').toUpperCase();
    if (oType.includes('COLLECTION') || oType === 'CC') {
      oType = 'CC';
    } else {
      oType = 'LAB';
    }

    let rCode = config.roleCode || config.role || 'SUBMITTER';
    if (rCode === 'CC_ONBOARD' || rCode === 'LAB_ONBOARD' || rCode === 'ONBOARD') {
      rCode = 'SUBMITTER';
    } else if (rCode === 'BUSINESS') {
      rCode = 'BUSINESS_APPROVER';
    }

    const rawStateVal = config.stateCode || config.state_code || config.state || config.stateName || config.state_name || '';
    const rawStateName = config.stateName || config.state_name || (config.state && String(config.state).length > 3 ? config.state : '') || '';
    const rawStateCode = config.stateCode || config.state_code || (config.state && String(config.state).length <= 3 ? String(config.state).toUpperCase() : '') || '';

    const matched = findMatchingState(rawStateVal || rawStateName || rawStateCode, stateList);
    const resolvedStateCode = matched
      ? (matched.stateCode || matched.state_code || matched.code || rawStateCode || '')
      : (rawStateCode || (rawStateVal && rawStateVal.length <= 3 ? rawStateVal.toUpperCase() : ''));
    const resolvedStateName = matched
      ? (matched.stateName || matched.state_name || matched.name || matched.label || rawStateName || resolvedStateCode)
      : (rawStateName || resolvedStateCode || 'All States (Default)');

    setWorkflowForm({
      id: config.id,
      onboardingType: oType,
      roleCode: rCode,
      stateCode: resolvedStateCode,
      stateName: resolvedStateCode ? resolvedStateName : 'All States (Default)',
      autoApprove: config.autoApprove !== undefined ? Number(config.autoApprove) : 0,
      status: config.status !== undefined ? Number(config.status) : 1,
    });
  };

  const handleAddNewConfig = () => {
    setEditingConfig(null);
    setShowAddConfigForm(true);
    setWorkflowForm((prev) => ({
      id: null,
      onboardingType: 'LAB',
      roleCode: prev.roleCode || 'SUBMITTER',
      stateCode: '',
      stateName: 'All States (Default)',
      autoApprove: 0,
      status: 1,
    }));
  };

  const handleCancelEdit = () => {
    setEditingConfig(null);
    setShowAddConfigForm(false);
    const uCode = selectedUser?.userCode || selectedUser?.code;
    loadUserConfig(uCode);
  };

  const handleToggleConfigStatus = async (config) => {
    const uCode = config?.userCode || config?.userId || selectedUser?.userCode || selectedUser?.code || selectedUser?.userId;
    const actorUser = currentUserCode() || 'ADMIN';

    try {
      if (config.status === 1) {
        await onboardingConfigApi.deactivateConfig(config.id, actorUser);
        toast.success('Configuration deactivated successfully!');
      } else {
        try {
          await onboardingConfigApi.restoreConfig(config.id, actorUser);
          toast.success('Configuration restored successfully!');
        } catch (restoreErr) {
          const oType = String(config.onboardingType || 'LAB').toUpperCase().includes('COLLECTION') || config.onboardingType === 'CC' ? 'CC' : 'LAB';
          const rCode = config.roleCode || config.role || 'SUBMITTER';
          const payload = {
            id: config.id,
            userCode: uCode,
            onboardingType: oType,
            roleCode: rCode,
            stateCode: config.stateCode || '',
            autoApprove: Number(config.autoApprove || 0),
            status: 1,
            updatedBy: actorUser,
          };
          await onboardingConfigApi.saveWorkflowConfig(payload);
          toast.success('Configuration activated successfully!');
        }
      }
      if (uCode) await loadUserConfig(uCode);
      if (viewMode === 'all') await loadAllConfigurations(allConfigPagination.page, allConfigPagination.size, allConfigTab);
      fetchUsers(searchQuery);
    } catch (err) {
      const errMsg = err?.response?.data?.responseMessage || err?.message || 'Failed to update status';
      console.error('Error toggling config status:', errMsg);
      toast.error(errMsg);
    }
  };

  const handleWorkflowReset = () => {
    const uCode = selectedUser?.userCode || selectedUser?.code;
    loadUserConfig(uCode);
    toast.info('Form reset to saved configuration');
  };

  const handleRemoveScreen = (screenIdToRemove) => {
    const targetId = typeof screenIdToRemove === 'object' ? Number(screenIdToRemove.screen) : Number(screenIdToRemove);
    setAssignedScreens((prev) => prev.filter((s) => Number(s.screen) !== targetId));
  };

  const handleAddScreen = (screenObj) => {
    const screenId = Number(screenObj.screen);
    if (assignedScreens.some((s) => Number(s.screen) === screenId)) {
      return;
    }
    setAssignedScreens((prev) => [...prev, screenObj]);
  };

  const handleSaveScreens = async () => {
    if (!selectedUser) return;
    const uId = selectedUser.userCode || selectedUser.userId || selectedUser.code || selectedUser.id;
    const screenIds = assignedScreens
      .map((s) => Number(s.screen))
      .filter((n) => !isNaN(n));

    try {
      setSubmittingScreens(true);
      const payload = {
        userId: uId,
        screens: screenIds,
        createdBy: currentUserCode() || 'ADMIN',
      };
      await onboardingConfigApi.saveUserScreens(payload);
      toast.success('Screen configuration updated successfully');
      await loadUserScreens(uId);
      await loadUserConfigLogs(uId, 0, configLogsPagination.size);
    } catch (err) {
      console.error('Error updating screens:', err);
      toast.error(err.message || 'Failed to update screens');
    } finally {
      setSubmittingScreens(false);
    }
  };

  const handleCancelScreens = () => {
    setAssignedScreens([...initialAssignedScreens]);
  };

  const hasScreenChanges = useMemo(() => {
    if (assignedScreens.length !== initialAssignedScreens.length) return true;
    const currentIds = new Set(assignedScreens.map((s) => Number(s.screen)));
    return initialAssignedScreens.some((s) => !currentIds.has(Number(s.screen)));
  }, [assignedScreens, initialAssignedScreens]);

  const availableScreensFiltered = useMemo(() => {
    const assignedIds = new Set(assignedScreens.map((s) => Number(s.screen)));
    return (masterScreens || []).filter((screenObj) => {
      const sNum = Number(screenObj.screen);
      const sName = screenObj.screenName || `Screen ${sNum}`;
      const notAssigned = !assignedIds.has(sNum);
      const matchesSearch = sName.toLowerCase().includes(screenSearchFilter.toLowerCase());
      return notAssigned && matchesSearch;
    });
  }, [masterScreens, assignedScreens, screenSearchFilter]);

  const selectedUserDisplayName =
    userConfigData?.userName ||
    selectedUser?.userName ||
    selectedUser?.fullName ||
    `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim() ||
    selectedUser?.userCode ||
    'Selected User';

  const avatarColor = getAvatarColor(selectedUserDisplayName);

  const onboardingTypeOptions = useMemo(() => {
    if (userConfigData?.options?.onboardingTypes && Array.isArray(userConfigData.options.onboardingTypes)) {
      return userConfigData.options.onboardingTypes;
    }
    return [
      { value: 'CC', label: 'Collection Center' },
      { value: 'LAB', label: 'Lab' },
    ];
  }, [userConfigData]);

  const roleOptions = useMemo(() => {
    let opts = DEFAULT_ROLE_OPTIONS;
    if (userConfigData?.options?.roles && Array.isArray(userConfigData.options.roles)) {
      opts = userConfigData.options.roles;
    }
    if (workflowForm.roleCode && !opts.some((o) => o.value === workflowForm.roleCode)) {
      opts = [...opts, { value: workflowForm.roleCode, label: workflowForm.roleCode }];
    }
    return opts;
  }, [userConfigData, workflowForm.roleCode]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#F8FAFC', minHeight: 'calc(100vh - 64px)' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          borderBottom: `2px solid ${C.border}`,
          mb: 3,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: { sm: 2 }, borderRight: { sm: `2px solid ${C.border}` } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: C.textDark, whiteSpace: 'nowrap' }}>
            Configuration
          </Typography>
          <Tooltip title="Configure user onboarding workflow permissions and screen access rights">
            <InfoIcon sx={{ color: C.textMuted, fontSize: 20, cursor: 'pointer' }} />
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            onClick={() => {
              setActiveTab(0);
              setRightPanelMode('config');
            }}
            sx={{
              py: 1.5,
              px: 2.5,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '15px',
              color: activeTab === 0 ? C.primary : C.textSecondary,
              borderBottom: activeTab === 0 ? `3px solid ${C.primary}` : '3px solid transparent',
              mb: '-2px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '&:hover': {
                color: C.primary,
              },
            }}
          >
            <SettingsIcon sx={{ fontSize: 18 }} />
            Screen Configuration
          </Box>

          <Box
            onClick={() => {
              setActiveTab(1);
              setRightPanelMode('config');
            }}
            sx={{
              py: 1.5,
              px: 2.5,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '15px',
              color: activeTab === 1 ? C.primary : C.textSecondary,
              borderBottom: activeTab === 1 ? `3px solid ${C.primary}` : '3px solid transparent',
              mb: '-2px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '&:hover': {
                color: C.primary,
              },
            }}
          >
            <TuneIcon sx={{ fontSize: 18 }} />
            Approver Configuration
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4} lg={3.8}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: '16px',
              border: `1px solid ${C.border}`,
              backgroundColor: '#FFFFFF',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            <Box
              component="form"
              onSubmit={handleSearchSubmit}
              sx={{ display: 'flex', gap: 1, mb: 2 }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search user by name, email, code..."
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchSubmit();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        size="small"
                        onClick={handleSearchSubmit}
                        edge="start"
                        title="Search"
                        sx={{
                          p: 0.5,
                          color: searchQuery ? C.primary : C.textMuted,
                          cursor: 'pointer',
                          '&:hover': {
                            color: C.primary,
                          },
                        }}
                      >
                        <SearchIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                        title="Clear search"
                        sx={{
                          p: 0.5,
                          color: C.textMuted,
                          cursor: 'pointer',
                          '&:hover': {
                            color: C.danger,
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#FFFFFF',
                    fontSize: '13px',
                    '& fieldset': {
                      borderColor: '#94A3B8',
                    },
                    '&:hover fieldset': {
                      borderColor: '#475569',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: C.primary,
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSearchSubmit}
                sx={{
                  borderRadius: '10px',
                  backgroundColor: C.primary,
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '13px',
                  textTransform: 'none',
                  px: 2,
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: C.primaryHover,
                  },
                }}
              >
                Search
              </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, px: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.textDark }}>
                Users
              </Typography>
              <Tooltip title="Refresh Users">
                <IconButton size="small" onClick={() => fetchUsers(searchQuery)} disabled={loadingUsers}>
                  <RefreshIcon sx={{ fontSize: 18, color: C.textMuted }} />
                </IconButton>
              </Tooltip>
            </Box>

            {activeTab === 1 && (
              <Box sx={{ mb: 2 }}>
                <Button
                  fullWidth
                  variant={viewMode === 'all' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<PeopleIcon sx={{ fontSize: 18 }} />}
                  onClick={() => {
                    setViewMode('all');
                    loadAllConfigurations(0, allConfigPagination.size, allConfigTab);
                  }}
                  sx={{
                    py: 0.9,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '12.5px',
                    backgroundColor: viewMode === 'all' ? C.primary : '#FFFFFF',
                    color: viewMode === 'all' ? '#FFFFFF' : C.primary,
                    borderColor: C.primary,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: viewMode === 'all' ? C.primaryHover : C.primaryLight,
                      borderColor: C.primary,
                    },
                  }}
                >
                  View All User Configurations
                </Button>
              </Box>
            )}

            {loadingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={32} sx={{ color: C.primary }} />
              </Box>
            ) : users.length === 0 ? (
              <Box sx={{ py: 8, textAlign: 'center', color: C.textSecondary }}>
                <Typography variant="body2">No users found</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.2,
                  maxHeight: 'calc(100vh - 240px)',
                  overflowY: 'auto',
                  pr: 0.5,
                  '&::-webkit-scrollbar': { width: '5px' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#E2E8F0', borderRadius: '4px' },
                }}
              >
                {users.map((user) => {
                  const isSelected =
                    (selectedUser?.userCode || selectedUser?.code || selectedUser?.id) ===
                    (user.userCode || user.code || user.id);
                  const name =
                    user.userName ||
                    user.fullName ||
                    `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                    user.userCode;
                  const initials = getInitials(name);
                  const avatarTheme = getAvatarColor(name);
                  const isConfigured = user.configured || (user.configurationCount && user.configurationCount > 0);

                  return (
                    <Box
                      key={user.id || user.code || user.userCode}
                      onClick={() => {
                        setSelectedUser(user);
                        if (viewMode === 'all') {
                          setViewMode('selected');
                        }
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: isSelected && viewMode === 'selected' ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                        backgroundColor: isSelected && viewMode === 'selected' ? '#F0F7FF' : '#FFFFFF',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: C.primary,
                          backgroundColor: isSelected && viewMode === 'selected' ? '#F0F7FF' : '#F8FAFC',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '50%',
                            backgroundColor: avatarTheme.bg,
                            color: avatarTheme.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '13px',
                            flexShrink: 0,
                          }}
                        >
                          {initials}
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{
                              fontWeight: 700,
                              color: isSelected && viewMode === 'selected' ? C.primary : C.textDark,
                              fontSize: '13.5px',
                              lineHeight: 1.2,
                            }}
                          >
                            {name}
                          </Typography>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              color: C.textSecondary,
                              display: 'block',
                              fontSize: '11.5px',
                              mt: 0.25,
                            }}
                          >
                            {user.userCode} &bull; {user.email || `${user.userCode}@curebay.com`}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1 }}>
                        {activeTab === 1 && (
                          isConfigured ? (
                            <Chip
                              label="Configured"
                              size="small"
                              sx={{
                                height: '20px',
                                fontSize: '10.5px',
                                fontWeight: 700,
                                backgroundColor: C.successBg,
                                color: C.successText,
                                borderRadius: '6px',
                              }}
                            />
                          ) : (
                            <Chip
                              label="Not Configured"
                              size="small"
                              sx={{
                                height: '20px',
                                fontSize: '10.5px',
                                fontWeight: 600,
                                backgroundColor: '#F1F5F9',
                                color: C.textSecondary,
                                borderRadius: '6px',
                              }}
                            />
                          )
                        )}
                        <ArrowForwardIosIcon sx={{ fontSize: 10, color: isSelected && viewMode === 'selected' ? C.primary : C.textMuted }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8} lg={8.2}>
          {activeTab === 1 && viewMode === 'all' ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: '16px',
                border: `1px solid ${C.border}`,
                backgroundColor: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                position: 'relative',
              }}
            >
              {loadingAllConfigs && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px',
                  }}
                >
                  <CircularProgress size={32} sx={{ color: C.primary }} />
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5, borderBottom: `1px solid ${C.borderLight}`, pb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: C.textDark, fontSize: '16px' }}>
                    Configured Chains
                  </Typography>
                  <Tooltip title="Refresh configurations">
                    <IconButton
                      size="small"
                      onClick={() => loadAllConfigurations(allConfigPagination.page, allConfigPagination.size, allConfigTab)}
                      disabled={loadingAllConfigs}
                    >
                      <RefreshIcon sx={{ fontSize: 18, color: C.textMuted }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Tabs
                  value={allConfigTab}
                  onChange={handleAllConfigTabChange}
                  sx={{
                    minHeight: '38px',
                    '& .MuiTab-root': {
                      minHeight: '38px',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '13.5px',
                      py: 0.5,
                      px: 2.5,
                      minWidth: 70,
                      color: C.textSecondary,
                    },
                    '& .Mui-selected': {
                      color: `${C.primary} !important`,
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: C.primary,
                      height: 3,
                      borderRadius: '3px 3px 0 0',
                    },
                  }}
                >
                  <Tab value="CC" label="CC" />
                  <Tab value="LAB" label="Lab" />
                </Tabs>
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'auto', maxHeight: 'calc(100vh - 300px)', minHeight: 320 }}>
                <Table stickyHeader size="small">
                  <TableHead sx={{ backgroundColor: '#F8FAFC', '& .MuiTableCell-root': { backgroundColor: '#F8FAFC', fontWeight: 700 } }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 160 }}>USERNAME</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 140 }}>ONBOARDING TYPE</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 130 }}>ROLE</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 130 }}>STATE</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 90 }}>STATUS</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 80 }}>ACTIONS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAllConfigs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 5, color: C.textSecondary }}>
                          {loadingAllConfigs
                            ? 'Loading all user configurations...'
                            : allConfigTab === 'CC'
                            ? 'No Collection Centre configurations found'
                            : 'No Lab configurations found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAllConfigs.map((cfg, index) => {
                        const oType = String(cfg.onboardingType || '').toUpperCase();
                        const isCC = oType.includes('COLLECTION') || oType === 'CC';
                        const stateDisplay = cfg.allStates || !cfg.stateCode ? 'All States (Default)' : (cfg.stateName || cfg.stateCode);
                        const rowUserName = cfg.userName || cfg.userCode || 'User';
                        const rowUserCode = cfg.userCode || cfg.userId;
                        const userTheme = getAvatarColor(rowUserName);

                        return (
                          <TableRow
                            key={cfg.id || `${cfg.userCode}-${index}`}
                            sx={{
                              '&:hover': { backgroundColor: '#F8FAFC' },
                            }}
                          >
                            <TableCell sx={{ minWidth: 160 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Box
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: '50%',
                                    backgroundColor: userTheme.bg,
                                    color: userTheme.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 700,
                                    fontSize: '11px',
                                    flexShrink: 0,
                                  }}
                                >
                                  {getInitials(rowUserName)}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '13px',
                                      color: C.textDark,
                                      lineHeight: 1.2,
                                    }}
                                    noWrap
                                  >
                                    {rowUserName}
                                  </Typography>
                                  {rowUserCode && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: C.textMuted,
                                        fontSize: '11px',
                                        display: 'block',
                                        lineHeight: 1.2,
                                        mt: 0.25,
                                      }}
                                      noWrap
                                    >
                                      {rowUserCode}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                label={cfg.onboardingTypeLabel || (isCC ? 'Collection Center' : 'Lab')}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '11.5px',
                                  backgroundColor: isCC ? '#FDF4FF' : '#EFF6FF',
                                  color: isCC ? '#9333EA' : C.primary,
                                  border: `1px solid ${isCC ? '#E9D5FF' : '#BFDBFE'}`,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: C.textDark }}>
                              {cfg.roleName || cfg.roleCode || 'Submitter'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '13px', color: C.textDark }}>
                              {stateDisplay}
                            </TableCell>
                            <TableCell>
                              <Tooltip title={cfg.status === 1 ? 'Click to deactivate' : 'Click to restore'}>
                                <Chip
                                  size="small"
                                  label={cfg.status === 1 ? 'Active' : 'Inactive'}
                                  onClick={() => handleToggleConfigStatus(cfg)}
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '11px',
                                    height: '22px',
                                    cursor: 'pointer',
                                    backgroundColor: cfg.status === 1 ? C.successBg : C.dangerBg,
                                    color: cfg.status === 1 ? C.successText : C.dangerText,
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit this configuration">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditConfigRow(cfg)}
                                  sx={{ color: C.textSecondary, '&:hover': { color: C.primary } }}
                                >
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={paginationCount}
                page={allConfigPagination.totalElements > 0 && allConfigPagination.page * allConfigPagination.size >= allConfigPagination.totalElements ? 0 : allConfigPagination.page}
                onPageChange={(e, newPage) => handlePageChange(newPage)}
                rowsPerPage={allConfigPagination.size}
                onRowsPerPageChange={(e) => handleRowsPerPageChange(parseInt(e.target.value, 10))}
                rowsPerPageOptions={[10, 25, 50, 100]}
                nextIconButtonProps={{
                  disabled:
                    loadingAllConfigs ||
                    allConfigPagination.last ||
                    (allConfigPagination.totalElements > 0 &&
                      (allConfigPagination.page + 1) * allConfigPagination.size >= allConfigPagination.totalElements),
                }}
                backIconButtonProps={{
                  disabled: loadingAllConfigs || allConfigPagination.page === 0,
                }}
                labelDisplayedRows={({ from, to, count }) => {
                  return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
                }}
                sx={{
                  borderTop: `1px solid ${C.borderLight}`,
                  px: 1,
                  '& .MuiTablePagination-select': { fontSize: '13px' },
                  '& .MuiTablePagination-displayedRows': { fontSize: '13px', color: C.textSecondary, fontWeight: 600 },
                  '& .MuiTablePagination-selectLabel': { fontSize: '13px', color: C.textSecondary, fontWeight: 600 },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      color: C.primary,
                      '&.Mui-disabled': {
                        color: C.textMuted,
                      },
                    },
                  },
                }}
              />
            </Paper>
          ) : selectedUser ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: '16px',
                  border: `1px solid ${C.border}`,
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        backgroundColor: avatarColor.bg,
                        color: avatarColor.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '18px',
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(selectedUserDisplayName)}
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: C.textDark, fontSize: '18px' }}>
                          {selectedUserDisplayName}
                        </Typography>
                        <Chip
                          label={workflowForm.status === 1 ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            backgroundColor: workflowForm.status === 1 ? C.successBg : C.dangerBg,
                            color: workflowForm.status === 1 ? C.successText : C.dangerText,
                            fontWeight: 700,
                            fontSize: '11px',
                            height: '22px',
                            borderRadius: '12px',
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: C.textSecondary, mt: 0.25, fontSize: '13px' }}>
                        {selectedUser?.userCode} &nbsp;|&nbsp; {selectedUser?.email || (selectedUser?.userCode ? `${selectedUser.userCode}@curebay.com` : '')} &nbsp;|&nbsp;{' '}
                        {userConfigData?.userRoleCode || selectedUser?.userRoleCode || selectedUser?.roleCode || 'DIAGGROUPADMIN'}
                      </Typography>
                    </Box>
                  </Box>

                  {activeTab === 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 0.5,
                        backgroundColor: '#F1F5F9',
                        borderRadius: '12px',
                        border: '1px solid #CBD5E1',
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => setRightPanelMode('config')}
                        startIcon={<SettingsIcon sx={{ fontSize: '16px !important' }} />}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '13px',
                          px: 2,
                          py: 0.75,
                          backgroundColor: rightPanelMode === 'config' ? '#FFFFFF' : 'transparent',
                          color: rightPanelMode === 'config' ? C.primary : C.textSecondary,
                          boxShadow: rightPanelMode === 'config' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                          border: rightPanelMode === 'config' ? '1px solid #CBD5E1' : '1px solid transparent',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            backgroundColor: rightPanelMode === 'config' ? '#FFFFFF' : '#E2E8F0',
                            color: C.primary,
                          },
                        }}
                      >
                        Configuration
                      </Button>

                      <Button
                        size="small"
                        onClick={() => setRightPanelMode('log')}
                        startIcon={<HistoryIcon sx={{ fontSize: '16px !important' }} />}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '13px',
                          px: 2,
                          py: 0.75,
                          backgroundColor: rightPanelMode === 'log' ? '#FFFFFF' : 'transparent',
                          color: rightPanelMode === 'log' ? C.primary : C.textSecondary,
                          boxShadow: rightPanelMode === 'log' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                          border: rightPanelMode === 'log' ? '1px solid #CBD5E1' : '1px solid transparent',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            backgroundColor: rightPanelMode === 'log' ? '#FFFFFF' : '#E2E8F0',
                            color: C.primary,
                          },
                        }}
                      >
                        Configuration Log
                        {configLogs.length > 0 && (
                          <Chip
                            label={configLogsPagination.totalElements || configLogs.length}
                            size="small"
                            sx={{
                              ml: 1,
                              height: 20,
                              fontSize: '11px',
                              fontWeight: 700,
                              backgroundColor: rightPanelMode === 'log' ? C.primary : '#94A3B8',
                              color: '#FFFFFF',
                              pointerEvents: 'none',
                            }}
                          />
                        )}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Paper>

              {activeTab === 1 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: '16px',
                    border: `1px solid ${C.border}`,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    position: 'relative',
                  }}
                >
                  {loadingUserConfig && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '16px',
                      }}
                    >
                      <CircularProgress size={32} sx={{ color: C.primary }} />
                    </Box>
                  )}

                  <Box sx={{ mb: 3.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: C.textDark }}>
                        Configured Chains ({userConfigurations.length})
                      </Typography>

                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddNewConfig}
                        variant={!editingConfigId && showAddConfigForm ? 'outlined' : 'contained'}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '12.5px',
                          backgroundColor: !editingConfigId && showAddConfigForm ? 'transparent' : C.primary,
                          color: !editingConfigId && showAddConfigForm ? C.primary : '#FFFFFF',
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: !editingConfigId && showAddConfigForm ? C.primaryLight : C.primaryHover,
                          },
                        }}
                      >
                        Add Configuration
                      </Button>
                    </Box>

                    <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'auto', maxHeight: 'calc(100vh - 340px)', minHeight: 280 }}>
                      <Table stickyHeader size="small">
                        <TableHead sx={{ backgroundColor: '#F8FAFC', '& .MuiTableCell-root': { backgroundColor: '#F8FAFC', fontWeight: 700 } }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 160 }}>USERNAME</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 140 }}>ONBOARDING TYPE</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 130 }}>ROLE</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 130 }}>STATE</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 90 }}>STATUS</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', minWidth: 80 }}>ACTIONS</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userConfigurations.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 4, color: C.textSecondary }}>
                                No configurations found for this user
                              </TableCell>
                            </TableRow>
                          ) : (
                            userConfigurations.map((cfg, index) => {
                              const isEditingThis = editingConfigId === cfg.id;
                              const oType = String(cfg.onboardingType || '').toUpperCase();
                              const isCC = oType.includes('COLLECTION') || oType === 'CC';
                              const stateDisplay = cfg.allStates || !cfg.stateCode ? 'All States (Default)' : (cfg.stateName || cfg.stateCode);
                              const rowUserName = cfg.userName || selectedUserDisplayName;
                              const rowUserCode = cfg.userCode || cfg.userId || selectedUser?.userCode;
                              const userTheme = getAvatarColor(rowUserName);

                              return (
                                <TableRow
                                  key={cfg.id || `${cfg.userCode}-${index}`}
                                  sx={{
                                    backgroundColor: isEditingThis ? '#EFF6FF' : 'inherit',
                                    '&:hover': { backgroundColor: isEditingThis ? '#EFF6FF' : '#F8FAFC' },
                                  }}
                                >
                                  <TableCell sx={{ minWidth: 160 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                      <Box
                                        sx={{
                                          width: 30,
                                          height: 30,
                                          borderRadius: '50%',
                                          backgroundColor: userTheme.bg,
                                          color: userTheme.color,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontWeight: 700,
                                          fontSize: '11px',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {getInitials(rowUserName)}
                                      </Box>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: '13px',
                                            color: C.textDark,
                                            lineHeight: 1.2,
                                          }}
                                          noWrap
                                        >
                                          {rowUserName}
                                        </Typography>
                                        {rowUserCode && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: C.textMuted,
                                              fontSize: '11px',
                                              display: 'block',
                                              lineHeight: 1.2,
                                              mt: 0.25,
                                            }}
                                            noWrap
                                          >
                                            {rowUserCode}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </TableCell>

                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={cfg.onboardingTypeLabel || (isCC ? 'Collection Center' : 'Lab')}
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: '11.5px',
                                        backgroundColor: isCC ? '#FDF4FF' : '#EFF6FF',
                                        color: isCC ? '#9333EA' : C.primary,
                                        border: `1px solid ${isCC ? '#E9D5FF' : '#BFDBFE'}`,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: C.textDark }}>
                                    {cfg.roleName || cfg.roleCode || 'Submitter'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '13px', color: C.textDark }}>
                                    {stateDisplay}
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title={cfg.status === 1 ? 'Click to deactivate (soft delete)' : 'Click to restore'}>
                                      <Chip
                                        size="small"
                                        label={cfg.status === 1 ? 'Active' : 'Inactive'}
                                        onClick={() => handleToggleConfigStatus(cfg)}
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: '11px',
                                          height: '22px',
                                          cursor: 'pointer',
                                          backgroundColor: cfg.status === 1 ? C.successBg : C.dangerBg,
                                          color: cfg.status === 1 ? C.successText : C.dangerText,
                                        }}
                                      />
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Tooltip title="Edit this configuration">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditConfigRow(cfg)}
                                        sx={{ color: isEditingThis ? C.primary : C.textSecondary }}
                                      >
                                        <EditIcon sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  <Box
                    component="form"
                    onSubmit={handleWorkflowSubmit}
                    sx={{
                      maxWidth: { xs: '100%', sm: 620, md: 680 },
                      mx: 'auto',
                      px: { xs: 1, sm: 3, md: 4 },
                      py: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: C.textDark }}>
                        {editingConfigId
                          ? `Edit Configuration (#${editingConfigId}) for ${selectedUserDisplayName}`
                          : `Workflow Configuration (${selectedUserDisplayName})`}
                      </Typography>
                      {editingConfigId && (
                        <Button
                          size="small"
                          onClick={handleCancelEdit}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12px', color: C.textSecondary }}
                        >
                          Cancel Edit
                        </Button>
                      )}
                    </Box>

                    <Grid container spacing={2.5} sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="onboarding-type-label" sx={{ fontWeight: 600, fontSize: '14px', color: '#334155' }}>
                            Onboarding Type *
                          </InputLabel>
                          <Select
                            labelId="onboarding-type-label"
                            id="onboardingType"
                            value={workflowForm.onboardingType}
                            label="Onboarding Type *"
                            onChange={(e) => handleWorkflowChange('onboardingType', e.target.value)}
                            sx={{
                              borderRadius: '10px',
                              backgroundColor: '#FFFFFF',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#94A3B8',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#475569',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: C.primary,
                              },
                            }}
                          >
                            {onboardingTypeOptions.map((t) => (
                              <MenuItem key={t.value} value={t.value}>
                                {t.label || (t.value === 'CC' ? 'Collection Center' : 'Lab')}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="role-label" sx={{ fontWeight: 600, fontSize: '14px', color: '#334155' }}>
                            Role *
                          </InputLabel>
                          <Select
                            labelId="role-label"
                            id="roleCode"
                            value={workflowForm.roleCode}
                            label="Role *"
                            onChange={(e) => handleWorkflowChange('roleCode', e.target.value)}
                            sx={{
                              borderRadius: '10px',
                              backgroundColor: '#FFFFFF',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#94A3B8',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#475569',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: C.primary,
                              },
                            }}
                          >
                            {roleOptions.map((r) => (
                              <MenuItem key={r.value} value={r.value}>
                                {r.label || r.roleName || r.value}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <Autocomplete
                          options={stateOptionsWithAll}
                          loading={loadingStates}
                          getOptionLabel={(option) =>
                            typeof option === 'string'
                              ? option
                              : option?.stateName || option?.name || (option?.stateCode === '' ? 'All States (Default)' : '') || ''
                          }
                          isOptionEqualToValue={(option, value) => {
                            if (!option || !value) return false;
                            const optCode = String(option.stateCode || option.code || option.stateName || '').toUpperCase();
                            const valCode = String(value.stateCode || value.code || value.stateName || '').toUpperCase();
                            return optCode === valCode;
                          }}
                          value={
                            stateOptionsWithAll.find((s) => {
                              if (!workflowForm.stateCode && !workflowForm.stateName) {
                                return s.stateCode === '';
                              }
                              if (workflowForm.stateName === 'All States (Default)' || workflowForm.stateCode === '') {
                                return s.stateCode === '';
                              }
                              const sCode = String(s.stateCode || s.code || '').toUpperCase();
                              const sName = String(s.stateName || s.name || '').toLowerCase();
                              const curCode = String(workflowForm.stateCode || '').toUpperCase();
                              const curName = String(workflowForm.stateName || '').toLowerCase();

                              return (
                                (curCode && sCode === curCode) ||
                                (curName && sName === curName) ||
                                (curCode && sName === curCode.toLowerCase()) ||
                                (curName && sCode === curName.toUpperCase()) ||
                                (sCode === 'OD' && curCode === 'OR') ||
                                (sCode === 'OR' && curCode === 'OD') ||
                                (sCode === 'OD' && curName === 'odisha') ||
                                (sName === 'odisha' && (curCode === 'OD' || curCode === 'OR'))
                              );
                            }) || stateOptionsWithAll[0]
                          }
                          onChange={handleStateSelect}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label="State (Default: All States)"
                              placeholder="Select state or leave as All States..."
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingStates ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '10px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderColor: '#94A3B8',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#475569',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: C.primary,
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: '#334155',
                                  fontWeight: 600,
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', pt: 1 }}>
                          <FormControl component="fieldset" sx={{ alignItems: 'center' }}>
                            <FormLabel component="legend" sx={{ fontWeight: 700, fontSize: '13.5px', color: '#1E293B', mb: 1, textAlign: 'center' }}>
                              Status *
                            </FormLabel>
                            <RadioGroup
                              row
                              name="status"
                              value={workflowForm.status}
                              onChange={(e) => handleWorkflowChange('status', Number(e.target.value))}
                              sx={{ justifyContent: 'center' }}
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  px: 2.5,
                                  py: 0.75,
                                  mr: 2,
                                  borderRadius: '10px',
                                  border: workflowForm.status === 1 ? `2px solid ${C.success}` : `1px solid #94A3B8`,
                                  backgroundColor: workflowForm.status === 1 ? C.successBg : '#FFFFFF',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                  '&:hover': {
                                    borderColor: C.success,
                                  },
                                }}
                                onClick={() => handleWorkflowChange('status', 1)}
                              >
                                <FormControlLabel
                                  value={1}
                                  control={<Radio size="small" sx={{ color: C.success }} />}
                                  label={<Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', color: workflowForm.status === 1 ? C.successText : 'inherit' }}>Active</Typography>}
                                  sx={{ m: 0 }}
                                />
                              </Paper>

                              <Paper
                                elevation={0}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  px: 2.5,
                                  py: 0.75,
                                  borderRadius: '10px',
                                  border: workflowForm.status === 0 ? `2px solid ${C.danger}` : `1px solid #94A3B8`,
                                  backgroundColor: workflowForm.status === 0 ? C.dangerBg : '#FFFFFF',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                  '&:hover': {
                                    borderColor: C.danger,
                                  },
                                }}
                                onClick={() => handleWorkflowChange('status', 0)}
                              >
                                <FormControlLabel
                                  value={0}
                                  control={<Radio size="small" sx={{ color: C.danger }} />}
                                  label={<Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', color: workflowForm.status === 0 ? C.dangerText : 'inherit' }}>Inactive</Typography>}
                                  sx={{ m: 0 }}
                                />
                              </Paper>
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 4, pt: 2, borderTop: `1px solid #94A3B8` }}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={editingConfigId ? handleCancelEdit : handleWorkflowReset}
                        sx={{
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontWeight: 600,
                          color: '#475569',
                          borderColor: '#94A3B8',
                          px: 3,
                          '&:hover': {
                            borderColor: '#334155',
                            backgroundColor: '#F8FAFC',
                          },
                        }}
                      >
                        {editingConfigId ? 'Cancel Edit' : 'Reset Form'}
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={submittingWorkflow}
                        sx={{
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontWeight: 700,
                          backgroundColor: C.primary,
                          color: '#FFFFFF',
                          px: 4,
                          py: 1,
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: C.primaryHover,
                            boxShadow: '0 4px 12px rgba(28, 108, 229, 0.25)',
                          },
                        }}
                      >
                        {submittingWorkflow ? (
                          <CircularProgress size={22} sx={{ color: '#FFFFFF' }} />
                        ) : editingConfigId ? (
                          'Save Changes'
                        ) : (
                          'Submit'
                        )}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              )}

              {rightPanelMode === 'config' && activeTab === 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: '16px',
                    border: `1px solid ${C.border}`,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    position: 'relative',
                  }}
                >
                  {(loadingUserScreens || loadingMasterScreens) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '16px',
                      }}
                    >
                      <CircularProgress size={32} sx={{ color: C.primary }} />
                    </Box>
                  )}

                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: C.textDark }}>
                        Assigned Screens
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<HistoryIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => setRightPanelMode('log')}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '12px',
                          borderColor: '#94A3B8',
                          color: C.primary,
                          py: 0.4,
                          px: 1.5,
                          '&:hover': {
                            borderColor: C.primary,
                            backgroundColor: '#EFF6FF',
                          },
                        }}
                      >
                        View Screen Config Log
                      </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, minHeight: 44, alignItems: 'center' }}>
                      {assignedScreens.length === 0 ? (
                        <Typography variant="body2" sx={{ color: C.textMuted, fontStyle: 'italic', py: 1 }}>
                          No screens currently assigned to this user. Select from the available list below to add.
                        </Typography>
                      ) : (
                        assignedScreens.map((screen) => {
                          const sNum = Number(screen.screen);
                          const sName = screen.screenName || `Screen ${sNum}`;
                          return (
                            <Chip
                              key={sNum}
                              label={sName}
                              onDelete={() => handleRemoveScreen(sNum)}
                              deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                backgroundColor: '#EFF6FF',
                                color: C.primary,
                                fontWeight: 600,
                                fontSize: '13px',
                                borderRadius: '8px',
                                border: `1px solid #BFDBFE`,
                                py: 0.75,
                                px: 0.5,
                                '& .MuiChip-deleteIcon': {
                                  color: C.primary,
                                  '&:hover': {
                                    color: '#DC2626',
                                  },
                                },
                              }}
                            />
                          );
                        })
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3, borderColor: '#CBD5E1' }} />

                  <Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: C.textDark }}>
                        Available Screens
                      </Typography>
                    </Box>

                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search available screens..."
                      value={screenSearchFilter}
                      onChange={(e) => setScreenSearchFilter(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ color: C.textMuted, fontSize: 18, mr: 1 }} />,
                      }}
                      sx={{
                        mb: 2.5,
                        maxWidth: 450,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          backgroundColor: '#FFFFFF',
                          fontSize: '13px',
                          '& fieldset': {
                            borderColor: '#94A3B8',
                          },
                          '&:hover fieldset': {
                            borderColor: '#475569',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: C.primary,
                          },
                        },
                      }}
                    />

                    <Grid container spacing={1.5}>
                      {availableScreensFiltered.length === 0 ? (
                        <Grid item xs={12}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              textAlign: 'center',
                              borderRadius: '10px',
                              backgroundColor: '#F8FAFC',
                              border: `1px dashed #CBD5E1`,
                            }}
                          >
                            <Typography variant="body2" sx={{ color: C.textMuted }}>
                              {masterScreens && masterScreens.length > 0 && assignedScreens.length === masterScreens.length
                                ? 'All available screens have been assigned to this user.'
                                : 'No matching screens found.'}
                            </Typography>
                          </Paper>
                        </Grid>
                      ) : (
                        availableScreensFiltered.map((screen) => {
                          const sNum = Number(screen.screen);
                          const sName = screen.screenName || `Screen ${sNum}`;
                          return (
                            <Grid item xs={12} sm={6} md={4} key={sNum}>
                              <Paper
                                elevation={0}
                                onClick={() => handleAddScreen(screen)}
                                sx={{
                                  p: 1.5,
                                  px: 2,
                                  borderRadius: '10px',
                                  border: `1px solid #94A3B8`,
                                  backgroundColor: '#FFFFFF',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                  '&:hover': {
                                    borderColor: C.primary,
                                    backgroundColor: '#F0F7FF',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 2px 8px rgba(28, 108, 229, 0.12)',
                                  },
                                }}
                              >
                                <Typography variant="body2" noWrap sx={{ fontSize: '13px', fontWeight: 600, color: C.textDark }}>
                                  {sName}
                                </Typography>
                                <Chip
                                  size="small"
                                  icon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Add"
                                  sx={{
                                    height: 24,
                                    fontSize: '11.5px',
                                    fontWeight: 700,
                                    backgroundColor: '#EFF6FF',
                                    color: C.primary,
                                    border: `1px solid #BFDBFE`,
                                    cursor: 'pointer',
                                  }}
                                />
                              </Paper>
                            </Grid>
                          );
                        })
                      )}
                    </Grid>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 2,
                        mt: 4,
                        pt: 2.5,
                        borderTop: '1px solid #E2E8F0',
                      }}
                    >
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={handleCancelScreens}
                        disabled={submittingScreens || !hasScreenChanges}
                        sx={{
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontWeight: 600,
                          color: '#475569',
                          borderColor: '#94A3B8',
                          px: 3,
                          '&:hover': {
                            borderColor: '#334155',
                            backgroundColor: '#F8FAFC',
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        onClick={handleSaveScreens}
                        disabled={submittingScreens || !hasScreenChanges}
                        sx={{
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontWeight: 700,
                          backgroundColor: C.primary,
                          color: '#FFFFFF',
                          px: 4,
                          py: 1,
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: C.primaryHover,
                            boxShadow: '0 4px 12px rgba(28, 108, 229, 0.25)',
                          },
                        }}
                      >
                        {submittingScreens ? (
                          <CircularProgress size={22} sx={{ color: '#FFFFFF' }} />
                        ) : (
                          'Update'
                        )}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              )}

              {activeTab === 0 && rightPanelMode === 'log' && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: '16px',
                    border: `1px solid ${C.border}`,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    position: 'relative',
                  }}
                >
                  {loadingConfigLogs && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '16px',
                      }}
                    >
                      <CircularProgress size={32} sx={{ color: C.primary }} />
                    </Box>
                  )}

                  {/* Header Bar */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2.5,
                      flexWrap: 'wrap',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: '10px',
                          backgroundColor: '#EFF6FF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: C.primary,
                          border: '1px solid #BFDBFE',
                        }}
                      >
                        <HistoryIcon sx={{ fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: C.textDark, lineHeight: 1.2 }}>
                          Configuration Log History
                        </Typography>
                        <Typography variant="caption" sx={{ color: C.textSecondary, fontSize: '12px' }}>
                          Workflow screen assignment logs for <strong>{selectedUserDisplayName} ({selectedUser?.userCode})</strong>
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title="Refresh Log">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const uCode = selectedUser?.userCode || selectedUser?.userId || selectedUser?.code || selectedUser?.id;
                            loadUserConfigLogs(uCode, configLogsPagination.page, configLogsPagination.size);
                          }}
                          disabled={loadingConfigLogs}
                          sx={{
                            border: `1px solid #CBD5E1`,
                            borderRadius: '8px',
                            p: 0.75,
                            height: 34,
                            width: 34,
                            '&:hover': {
                              borderColor: C.primary,
                              color: C.primary,
                              backgroundColor: '#EFF6FF',
                            },
                          }}
                        >
                          <RefreshIcon sx={{ fontSize: 18, color: C.textSecondary }} />
                        </IconButton>
                      </Tooltip>

                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setRightPanelMode('config')}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '12.5px',
                          borderColor: '#94A3B8',
                          color: C.textSecondary,
                          px: 1.5,
                          height: 34,
                          '&:hover': {
                            borderColor: C.primary,
                            backgroundColor: '#EFF6FF',
                            color: C.primary,
                          },
                        }}
                      >
                        Back to Configuration
                      </Button>
                    </Box>
                  </Box>

                  {/* Log Table */}
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: `1px solid ${C.border}`,
                      borderRadius: '12px',
                      overflow: 'auto',
                      maxHeight: 'calc(100vh - 350px)',
                      minHeight: 280,
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead sx={{ backgroundColor: '#F8FAFC', '& .MuiTableCell-root': { backgroundColor: '#F8FAFC', fontWeight: 700 } }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC' }}>
                            CONFIGURED SCREENS
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: C.textSecondary, backgroundColor: '#F8FAFC', width: 220 }}>
                            DATE
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {configLogs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center" sx={{ py: 6, color: C.textSecondary }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <HistoryIcon sx={{ fontSize: 36, color: C.textMuted }} />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  No configuration log records found
                                </Typography>
                                <Typography variant="caption" sx={{ color: C.textMuted }}>
                                  When screen configurations are updated for this user, the audit log will appear here.
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : (
                          configLogs.map((logItem, index) => {
                            const rawScreenName = logItem.screenName;
                            const rawScreens = logItem.screen;

                            let screenNames = typeof rawScreenName === 'string'
                              ? rawScreenName.split(',').map((s) => s.trim()).filter(Boolean)
                              : Array.isArray(rawScreenName)
                              ? rawScreenName
                              : [];

                            if (screenNames.length === 0 && rawScreens) {
                              const ids = typeof rawScreens === 'string'
                                ? rawScreens.split(',').map((s) => s.trim()).filter(Boolean)
                                : Array.isArray(rawScreens)
                                ? rawScreens
                                : [];
                              screenNames = ids.map((id) => {
                                const matched = masterScreens.find((ms) => String(ms.screen) === String(id));
                                return matched ? matched.screenName : `Screen ${id}`;
                              });
                            }

                            return (
                              <TableRow
                                key={logItem.id || index}
                                sx={{
                                  '&:hover': { backgroundColor: '#F8FAFC' },
                                }}
                              >
                                <TableCell>
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                      <Chip
                                        size="small"
                                        label={`${screenNames.length} ${screenNames.length === 1 ? 'Screen' : 'Screens'}`}
                                        sx={{
                                          fontSize: '11px',
                                          fontWeight: 700,
                                          backgroundColor: '#F1F5F9',
                                          color: C.textDark,
                                          height: 20,
                                          borderRadius: '4px',
                                        }}
                                      />
                                    </Box>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                      {screenNames.map((sName, sIdx) => (
                                        <Chip
                                          key={sIdx}
                                          size="small"
                                          label={sName}
                                          sx={{
                                            fontSize: '11.5px',
                                            fontWeight: 600,
                                            backgroundColor: '#EFF6FF',
                                            color: C.primary,
                                            border: '1px solid #BFDBFE',
                                            borderRadius: '6px',
                                            height: 24,
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                </TableCell>

                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px', color: C.textDark }}>
                                      {formatDateTime(logItem.createdDate)}
                                    </Typography>
                                    {logItem.createdDate && (
                                      <Typography variant="caption" sx={{ color: C.textMuted, fontSize: '11px', display: 'block' }}>
                                        {logItem.createdDate.replace('T', ' ')}
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={configLogsPagination.totalElements}
                    page={configLogsPagination.page}
                    onPageChange={handleLogPageChange}
                    rowsPerPage={configLogsPagination.size}
                    onRowsPerPageChange={handleLogRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25]}
                    sx={{
                      borderTop: `1px solid ${C.borderLight}`,
                      px: 1,
                      '& .MuiTablePagination-select': { fontSize: '13px' },
                      '& .MuiTablePagination-displayedRows': { fontSize: '13px', color: C.textSecondary, fontWeight: 600 },
                      '& .MuiTablePagination-selectLabel': { fontSize: '13px', color: C.textSecondary, fontWeight: 600 },
                      '& .MuiTablePagination-actions': {
                        '& .MuiIconButton-root': {
                          color: C.primary,
                          '&.Mui-disabled': {
                            color: C.textMuted,
                          },
                        },
                      },
                    }}
                  />
                </Paper>
              )}
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 6,
                borderRadius: '16px',
                border: `1px solid ${C.border}`,
                backgroundColor: '#FFFFFF',
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" sx={{ color: C.textSecondary }}>
                Please select a user from the list to view and configure settings.
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
